import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getTextField } from '@lender/utils';
import { Box } from '@mui/material';
import { TextFieldCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import { object } from 'yup';

import useForm from 'hooks/useForm';
import { getConditionalObject, prepareStringForComparison, trimBodyStringValues } from 'utils/data';
import { equalCustomizer } from 'utils/form';

import { createContactTypeAction, updateContactTypeAction } from 'store/contactTypes/actions';
import { selectBusyContactTypesRoles } from 'store/contactTypes/selectors';
import {
  closeDialogAction,
  showMessageAction,
  updateDialogOptionsAction,
} from 'store/global/actions';

import { ContactTypesTestIds } from '../ContactTypes.testIds';
import { TableCellId } from '../ContactTypesPage.constants';

import { NAME_MAX_LENGTH } from './ContactTypeForm.constants';

const formFields = [TableCellId.NAME];

const newContactTypeInitialForm = formFields.reduce((acc, field) => {
  acc[field] = '';

  return acc;
}, {});

const getSchema = ({ busyContactTypesRoles }) =>
  object().shape({
    [TableCellId.NAME]: getTextField({ isRequired: true })
      .max(NAME_MAX_LENGTH)
      .nullable()
      .test(
        'role-name-should-be-unique',
        'Role name should be unique',
        value =>
          !busyContactTypesRoles.some(
            role => prepareStringForComparison(role) === prepareStringForComparison(value),
          ),
      ),
  });

const ContactTypeForm = ({
  dialogId,
  contactTypeInitialData,
  isLoading,
  setIsLoading,
  afterAcceptCallback,
}) => {
  const dispatch = useDispatch();

  const busyContactTypesRoles = useSelector(
    selectBusyContactTypesRoles(contactTypeInitialData?.id),
  );

  const isEditMode = !!contactTypeInitialData;

  const initialForm = useMemo(
    () => (isEditMode ? _.pick(contactTypeInitialData, formFields) : newContactTypeInitialForm),
    [isEditMode, contactTypeInitialData],
  );

  const { form, formRef, handleChange, setSchema, validateForm, validationErrors } =
    useForm(initialForm);

  const isFormChanged = useMemo(
    () =>
      !_.isEqual(trimBodyStringValues(form), trimBodyStringValues(initialForm), equalCustomizer),
    [form, initialForm],
  );

  const onSubmit = useCallback(async () => {
    if (!(await validateForm({ noFormChangedValidate: true }))) {
      return;
    }

    setIsLoading(true);

    const action = isEditMode ? updateContactTypeAction : createContactTypeAction;

    const payload = {
      ...getConditionalObject(isEditMode, {
        id: contactTypeInitialData?.id,
      }),
      ...trimBodyStringValues(formRef.current),
    };

    dispatch(action(payload))
      .then(res => {
        dispatch(
          showMessageAction({
            message: `Contact Type ${payload[TableCellId.NAME]} is successfully ${
              isEditMode ? 'updated' : 'created'
            }`,
            variant: 'success',
          }),
        );
        afterAcceptCallback?.(res);
        dispatch(closeDialogAction(dialogId));
      })
      .catch(Error)
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    dispatch,
    dialogId,
    validateForm,
    formRef,
    setIsLoading,
    contactTypeInitialData,
    isEditMode,
    afterAcceptCallback,
  ]);

  useEffect(() => {
    setSchema(getSchema({ busyContactTypesRoles }));
  }, [setSchema, busyContactTypesRoles]);

  useEffect(() => {
    dispatch(
      updateDialogOptionsAction(dialogId, {
        isAcceptButtonDisabled: isLoading || !isFormChanged,
        onAcceptClick: onSubmit,
      }),
    );
  }, [dispatch, dialogId, isFormChanged, isLoading, onSubmit]);

  return (
    <Box className="w-full">
      <TextFieldCDS
        required
        withPopupError
        dataTestId={ContactTypesTestIds.FORM_NAME_INPUT}
        error={!!validationErrors[TableCellId.NAME]}
        helperText={validationErrors[TableCellId.NAME]}
        label="Role"
        maxLength={150}
        name={TableCellId.NAME}
        value={form[TableCellId.NAME]}
        width="100%"
        onChange={handleChange}
      />
    </Box>
  );
};

ContactTypeForm.propTypes = {
  ...WithLoadingPropTypes,
};

export default withLoading(ContactTypeForm);
